import { useMemo } from "react";
import {
  AcceptanceStatus,
  EmailInviteStatus,
  EWorkflowStatusDataBlock
} from "../../../constants";
import { IMaterial, IMaterialGroup } from "../type-definitions";
import useRequestDurationsDataProvider from "./request-duations-old-new-model-data-provider";
import { resolveWorkflowStatus } from "../material-request-duration-button/use-material-request-duration-button";

// --- Material status (invite / assignee state) ---

const REQUEST_SENT = EmailInviteStatus.REQUEST_SENT as string;
const isRequestAlreadySent = (m: IMaterial) =>
  m.estimate_invite_features[0]?.estimated_invite.delivery_status ===
  REQUEST_SENT;
const isInactiveUser = (m: IMaterial) =>
  m.material_assignee?.[0]?.user?.project_users?.[0]?.status?.id ===
  AcceptanceStatus.DEACTIVATED;
const isCompletedWorkflow = (m: IMaterial) =>
  resolveWorkflowStatus(m) === EWorkflowStatusDataBlock.WF_COMPLETED;

function filterMaterials(
  materials: IMaterial[]
): IMaterialGroup["materialsByStatus"] {
  const requestAlreadySentItems = materials.filter(isRequestAlreadySent);
  const allowedToSendRequestItems = materials.filter(
    (m) => !isRequestAlreadySent(m)
  );
  const inactiveUserItems = materials.filter(isInactiveUser);
  return {
    requestAlreadySentItems,
    allowedToSendRequestItems,
    inactiveUserItems
  };
}

// --- Assignee category (mutually exclusive; order of checks matters) ---

type MaterialCategory =
  | "completed_workflow"
  | "unassigned"
  | "unregistered"
  | "inactive_user"
  | "assigned";

function getMaterialCategory(material: IMaterial): MaterialCategory {
  if (isCompletedWorkflow(material)) return "completed_workflow";

  const hasAssignee = Boolean(material.material_assignee?.[0]?.user?.id);
  const hasUnregisteredName = Boolean(
    material.material_assignee?.[0]?.user_unregistered?.trim()
  );

  if (!hasAssignee && !hasUnregisteredName) return "unassigned";
  if (!hasAssignee && hasUnregisteredName) return "unregistered";
  if (hasAssignee && isInactiveUser(material)) return "inactive_user";
  if (hasAssignee && !isInactiveUser(material)) return "assigned";

  return "unassigned";
}

// --- Partition: one pass into buckets ---

interface AssignedBucket {
  companyId: string;
  assigneeId: string;
  materials: IMaterial[];
}

function partitionMaterials(materials: IMaterial[]): {
  completedWorkflow: IMaterial[];
  unassigned: IMaterial[];
  unregistered: IMaterial[];
  inactive: IMaterial[];
  assigned: AssignedBucket[];
} {
  const completedWorkflow: IMaterial[] = [];
  const unassigned: IMaterial[] = [];
  const unregistered: IMaterial[] = [];
  const inactive: IMaterial[] = [];
  const assignedByKey = new Map<string, AssignedBucket>();

  for (const material of materials) {
    const category = getMaterialCategory(material);

    switch (category) {
      case "completed_workflow":
        completedWorkflow.push(material);
        break;
      case "unassigned":
        unassigned.push(material);
        break;
      case "unregistered":
        unregistered.push(material);
        break;
      case "inactive_user":
        inactive.push(material);
        break;
      case "assigned": {
        const companyId =
          material.materials_trade_partner?.[0]?.vendor?.id ?? "no_company";
        const assigneeId = material.material_assignee![0].user!.id;
        const key = `${companyId}|${assigneeId}`;
        let bucket = assignedByKey.get(key);
        if (!bucket) {
          bucket = { companyId, assigneeId, materials: [] };
          assignedByKey.set(key, bucket);
        }
        bucket.materials.push(material);
        break;
      }
    }
  }

  return {
    completedWorkflow,
    unassigned,
    unregistered,
    inactive,
    assigned: Array.from(assignedByKey.values())
  };
}

// --- Build one group from a material list (for unassigned / unregistered / inactive) ---

function buildSpecialGroup(
  materials: IMaterial[],
  groupType: IMaterialGroup["groupType"],
  groupId: string
): IMaterialGroup | null {
  if (materials.length === 0) return null;
  return {
    groupType,
    groupId,
    subcontractorCompany: null,
    materials,
    assigneeUser: null,
    materialsByStatus: filterMaterials(materials)
  };
}

// --- Build assigned groups and sort by company then assignee name ---

function buildAssignedGroups(buckets: AssignedBucket[]): IMaterialGroup[] {
  const groups: IMaterialGroup[] = buckets.map((bucket) => {
    const first = bucket.materials[0];
    const user = first.material_assignee?.[0]?.user ?? null;
    const vendor = first.materials_trade_partner?.[0]?.vendor ?? null;
    return {
      groupType: "assignee_actual",
      groupId: user?.id ?? "",
      subcontractorCompany: vendor
        ? { name: vendor.name, id: vendor.id }
        : null,
      materials: bucket.materials,
      materialsByStatus: filterMaterials(bucket.materials),
      assigneeUser: user
    };
  });

  groups.sort((a, b) => {
    const companyA = a.subcontractorCompany?.name ?? "";
    const companyB = b.subcontractorCompany?.name ?? "";
    const byCompany = companyA.localeCompare(companyB);
    if (byCompany !== 0) return byCompany;
    const nameA = a.assigneeUser?.first_name ?? "";
    const nameB = b.assigneeUser?.first_name ?? "";
    return nameA.localeCompare(nameB);
  });

  return groups;
}

// --- Main grouping: partition then build all group types ---

function groupMaterials(materials: IMaterial[]): {
  completedWorkflowGroup: IMaterialGroup | null;
  assignedGroups: IMaterialGroup[];
  unregisteredGroup: IMaterialGroup | null;
  unassignedGroup: IMaterialGroup | null;
  inactiveUserGroup: IMaterialGroup | null;
} {
  const partition = partitionMaterials(materials);

  const completedWorkflowGroup = buildSpecialGroup(
    partition.completedWorkflow,
    "completed_workflow",
    "completed_workflow"
  );
  const unassignedGroup = buildSpecialGroup(
    partition.unassigned,
    "unassigned",
    "unassigned"
  );
  const unregisteredGroup = buildSpecialGroup(
    partition.unregistered,
    "assignee_unregistered",
    "unregistered"
  );
  const inactiveUserGroup = buildSpecialGroup(
    partition.inactive,
    "inactive_user",
    "inactive_user"
  );
  const assignedGroups = buildAssignedGroups(partition.assigned);

  return {
    completedWorkflowGroup,
    assignedGroups,
    unregisteredGroup,
    unassignedGroup,
    inactiveUserGroup
  };
}

function useRequestDurationsMulti(selectedItems: { id: string }[]) {
  const selectedIds = useMemo(
    () => selectedItems.map((item) => item.id),
    [selectedItems]
  );

  const { data, loading } = useRequestDurationsDataProvider(selectedIds);

  const structuredData = useMemo(() => {
    if (!data) return null;

    const groupedMaterials = groupMaterials(data.feature_instance);

    return { groupedMaterials };
  }, [data]);

  return {
    data: structuredData ? { ...structuredData } : null,
    loading
  };
}

export default useRequestDurationsMulti;
